'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetMarkets, useGetSearch, usePrefetchGetMarket, usePrefetchGetOutcomeChart, usePrefetchGetProposal, usePrefetchGetUser, usePrefetchGetWinner } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SearchGlobeButton } from '@/components/globe/globe-button';
import { LiveEventRowSearchItem, type LiveEventSearchResult } from '@/components/search/live-event-row-search-item';
import { MarketRowSearchItem } from '@/components/search/market-row-search-item';
import { OutcomeRow } from '@/components/search/outcome-row';
import { ProposalRowSearchItem } from '@/components/search/proposal-row-search-item';
import type { BlendedSearchResult, CreatorSearchResult, MarketSearchResult, OutcomeSearchResult, ProposalSearchResult, UserSearchResult, WinnerSearchResult } from '@/components/search/search-types';
import { UserSearchRow } from '@/components/search/user-search-row';
import { WinnerRow } from '@/components/search/winner-row';
import { SortPillRow } from '@/components/shared/sort-pill-row';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/app-command';
import { Icon } from '@/components/ui/icon';
import { CheckmarkCircle02IconSolid, Clock01Icon, Copy01IconSolid, DollarCircleIcon, Loading03Icon, SearchRemoveIcon } from '@/components/ui/icon/glyphs';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { useSearchQueryMode } from '@/hooks/search/use-search-query-mode';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { PROPOSAL_DETAIL_PREFETCH_FIELDS } from '@/lib/sdk/projections/launchpad';
import { PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections/portfolio';
import { TRENDING_SEARCH_LIMIT, TRENDING_SEARCH_STATUS } from '@/lib/sdk/projections/search';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { useLayoutStore } from '@/stores/layout';
import { useRecentSearchesStore } from '@/stores/recent-searches';
const DIALOG_CONTENT_CLASS = 'max-w-2xl overflow-hidden !p-0 !gap-0';
const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
type SearchTab = 'all' | 'markets' | 'live-events' | 'outcomes' | 'winners' | 'proposals' | 'users';

// Reorder per the search-overhaul mockup: All → Markets → Outcomes → Winners →
// Launchpad (proposals) → Users. `winners` is gated by the feed kill-switch.
const BASE_TAB_OPTIONS: ReadonlyArray<SearchTab> = ['all', 'markets', 'outcomes', 'winners', 'proposals', 'users'];
function winnerHref(winner: WinnerSearchResult): string {
  return `/winners/${winner.mint}`;
}
function userResultKey(result: UserSearchResult, idx: number): string {
  const {
    userId,
    walletAddress
  } = result.user;
  if (userId !== null && userId.length > 0) return userId;
  if (walletAddress.length > 0) return walletAddress;
  return `user-${idx}`;
}

// Shared row context threaded through the row-list components below. Extracting
// these from the inline `{renderXRows()}` calls keeps each list a stable sibling
// subtree (no remount/state-loss as the parent re-renders).
interface RowSharedProps {
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverMarket: (pubkey: string) => void;
  onHoverWinner: (mint: string) => void;
  onHoverProposal: (pubkey: string) => void;
  onHoverUser: (identifier: string) => void;
}
function buildMarketRow(m: Omit<MarketSearchResult, 'marketConfig'> & {
  marketConfig: string | undefined;
}, shared: Pick<RowSharedProps, 'tickerHighlighted' | 'onCommit' | 'onHoverMarket'>, exact: boolean = false): React.ReactNode {
  return <MarketRowSearchItem marketType={m.marketType} partnerCreatorId={m.partnerCreatorId} associatedCreator={m.associatedCreator} key={m.pubkey} pubkey={m.pubkey} title={m.title} coverImageUrl={m.coverImageUrl} coverImageNsfw={m.coverImageNsfw} status={m.status} marketConfig={m.marketConfig} volumeTotal={m.volumeTotal} prizePool={m.prizePool} numOutcomes={m.numOutcomes} ticker={m.ticker} tickerHighlighted={shared.tickerHighlighted} exact={exact} href={`/markets/${m.pubkey}`} onCommit={shared.onCommit} onHoverPrefetch={shared.onHoverMarket} />;
}
function buildProposalRow(p: Omit<ProposalSearchResult, 'marketConfig'> & {
  marketConfig: string | undefined;
}, shared: Pick<RowSharedProps, 'tickerHighlighted' | 'onCommit' | 'onHoverProposal'>): React.ReactNode {
  return <ProposalRowSearchItem marketType={p.marketType} partnerCreatorId={p.partnerCreatorId} associatedCreator={p.associatedCreator} key={p.proposalId} proposalId={p.proposalId} title={p.title} coverImageUrl={p.coverImageUrl} coverImageNsfw={p.coverImageNsfw} totalFunded={p.totalFunded} marketConfig={p.marketConfig} numOutcomes={p.numOutcomes} backerCount={p.backerCount} ticker={p.ticker} tickerHighlighted={shared.tickerHighlighted} href={`/proposals/${p.proposalId}`} onCommit={shared.onCommit} onHoverPrefetch={shared.onHoverProposal} />;
}
function buildUserRow(entry: UserSearchResult, idx: number, shared: Pick<RowSharedProps, 'onCommit' | 'onHoverUser'>): React.ReactNode {
  const u = entry.user;
  const portfolioId = u.userId ?? u.walletAddress;
  const key = userResultKey(entry, idx);
  return <UserSearchRow key={key} variant="user" rowKey={key} identifier={portfolioId} walletAddress={u.walletAddress} userId={u.userId} displayName={u.displayName} handle={u.handle} avatarUrl={u.avatarUrl} avatarNsfw={u.avatarNsfw === true} avatarPreferences={u.avatarPreferences ?? null} totalVolume={entry.totalVolume} totalTrades={entry.totalTrades} totalPnl={entry.totalPnl} partner={entry.partner} href={`/portfolio/${portfolioId}`} onCommit={shared.onCommit} onHoverPrefetch={shared.onHoverUser} />;
}
interface UserRowsListProps {
  users: ReadonlyArray<UserSearchResult>;
  onCommit: () => void;
  onHoverUser: (identifier: string) => void;
}
function UserRowsList({
  users,
  onCommit,
  onHoverUser
}: UserRowsListProps): ReactNode {
  return users.map((entry, idx) => buildUserRow(entry, idx, {
    onCommit,
    onHoverUser
  }));
}
interface MarketRowsListProps {
  markets: ReadonlyArray<MarketSearchResult>;
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverMarket: (pubkey: string) => void;
}
function MarketRowsList({
  markets,
  tickerHighlighted,
  onCommit,
  onHoverMarket
}: MarketRowsListProps): ReactNode {
  return markets.map(m => buildMarketRow(m, {
    tickerHighlighted,
    onCommit,
    onHoverMarket
  }));
}
interface ProposalRowsListProps {
  proposals: ReadonlyArray<ProposalSearchResult>;
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverProposal: (pubkey: string) => void;
}
function ProposalRowsList({
  proposals,
  tickerHighlighted,
  onCommit,
  onHoverProposal
}: ProposalRowsListProps): ReactNode {
  return proposals.map(p => buildProposalRow(p, {
    tickerHighlighted,
    onCommit,
    onHoverProposal
  }));
}
interface OutcomeRowsListProps {
  outcomes: ReadonlyArray<OutcomeSearchResult>;
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverMarket: (pubkey: string) => void;
}
function OutcomeRowsList({
  outcomes,
  tickerHighlighted,
  onCommit,
  onHoverMarket
}: OutcomeRowsListProps): ReactNode {
  return outcomes.map(outcome => <OutcomeRow key={outcome.pubkey} outcome={outcome} tickerHighlighted={tickerHighlighted} href={`/markets/${outcome.market}?outcome=${encodeURIComponent(outcome.label)}`} onCommit={onCommit} onHoverPrefetch={onHoverMarket} />);
}
interface WinnerRowsListProps {
  winners: ReadonlyArray<WinnerSearchResult>;
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverWinner: (mint: string) => void;
}
function WinnerRowsList({
  winners,
  tickerHighlighted,
  onCommit,
  onHoverWinner
}: WinnerRowsListProps): ReactNode {
  return winners.map(winner => <WinnerRow key={winner.pubkey} winner={winner} tickerHighlighted={tickerHighlighted} href={winnerHref(winner)} onCommit={onCommit} onHoverPrefetch={onHoverWinner} />);
}
interface CreatorRowsListProps {
  creators: ReadonlyArray<CreatorSearchResult>;
  onCommit: () => void;
  onHoverUser: (identifier: string) => void;
}
function CreatorRowsList({
  creators,
  onCommit,
  onHoverUser
}: CreatorRowsListProps): ReactNode {
  return creators.map(entry => {
    const creatorIdentifier = entry.creator.userId ?? entry.creator.walletAddress;
    return <UserSearchRow key={entry.creator.walletAddress} variant="creator" rowKey={entry.creator.walletAddress} identifier={creatorIdentifier} walletAddress={entry.creator.walletAddress} userId={entry.creator.userId ?? null} displayName={entry.creator.displayName ?? null} handle={entry.creator.handle ?? null} avatarUrl={entry.creator.avatarUrl ?? null} avatarNsfw={entry.creator.avatarNsfw === true} avatarPreferences={entry.creator.avatarPreferences ?? null} totalMarketsCreated={entry.totalMarketsCreated} href={`/portfolio/${entry.creator.walletAddress}`} onCommit={onCommit} onHoverPrefetch={onHoverUser} />;
  });
}
interface BlendedRowsListProps {
  blended: ReadonlyArray<BlendedSearchResult>;
  marketByPubkey: Map<string, MarketSearchResult>;
  proposalById: Map<string, ProposalSearchResult>;
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverMarket: (pubkey: string) => void;
  onHoverProposal: (pubkey: string) => void;
}
function BlendedRowsList({
  blended,
  marketByPubkey,
  proposalById,
  tickerHighlighted,
  onCommit,
  onHoverMarket,
  onHoverProposal
}: BlendedRowsListProps): ReactNode {
  const marketShared = {
    tickerHighlighted,
    onCommit,
    onHoverMarket
  };
  const proposalShared = {
    tickerHighlighted,
    onCommit,
    onHoverProposal
  };
  return blended.map(item => {
    if (item.resultType === 'market') {
      const enriched = marketByPubkey.get(item.id);
      if (enriched !== undefined) return buildMarketRow(enriched, marketShared);
      // Fallback when the typed lookup misses (rare sort drift).
      return buildMarketRow({
        pubkey: item.id,
        title: item.title,
        category: item.category,
        status: item.status,
        volumeTotal: '0',
        coverImageUrl: item.coverImageUrl,
        coverImageNsfw: item.coverImageNsfw,
        similarity: item.similarity,
        prizePool: '0',
        marketConfig: undefined,
        ticker: '',
        numOutcomes: 0
      }, marketShared);
    }
    const enriched = proposalById.get(item.id);
    if (enriched !== undefined) return buildProposalRow(enriched, proposalShared);
    return buildProposalRow({
      proposalId: item.id,
      title: item.title,
      category: item.category,
      status: item.status,
      totalFunded: '0',
      coverImageUrl: item.coverImageUrl,
      coverImageNsfw: item.coverImageNsfw,
      similarity: item.similarity,
      ticker: '',
      marketConfig: undefined,
      numOutcomes: 0,
      backerCount: 0
    }, proposalShared);
  });
}
interface SearchInputRowProps {
  query: string;
  mode: string;
  onQueryChange: (value: string) => void;
  onPaste: () => void;
}
function SearchInputRow(t0) {
  const $ = _c(27);
  const {
    query,
    mode,
    onQueryChange,
    onPaste
  } = t0;
  const t = useTranslations("search");
  let t1;
  if ($[0] !== t) {
    t1 = t("placeholder");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onQueryChange || $[3] !== query || $[4] !== t1) {
    t2 = <CommandInput placeholder={t1} value={query} onValueChange={onQueryChange} className="pr-28" />;
    $[2] = onQueryChange;
    $[3] = query;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== mode || $[7] !== t) {
    t3 = mode === "ca" ? <span className="inline-flex h-6 items-center gap-1 rounded-pill bg-background-brand px-2 text-micro font-semibold text-foreground-brand"><Icon icon={CheckmarkCircle02IconSolid} className="size-3" />{t("mode_ca")}</span> : null;
    $[6] = mode;
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== mode || $[10] !== t) {
    t4 = mode === "ticker" ? <span className="inline-flex h-6 items-center gap-1 rounded-pill bg-feedback-warning-accent/10 px-2 text-micro font-semibold text-feedback-warning-content"><Icon icon={DollarCircleIcon} className="size-3" />{t("mode_ticker")}</span> : null;
    $[9] = mode;
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== onPaste) {
    t5 = () => {
      onPaste();
    };
    $[12] = onPaste;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon={Copy01IconSolid} className="size-3" />;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("paste");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t7) {
    t8 = <AnimatedButton size="xs" variant="tertiary" className="border" onClick={t5}>{t6}{t7}</AnimatedButton>;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t8) {
    t9 = <div className="absolute right-3 top-0 flex h-9 items-center gap-2">{t3}{t4}{t8}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t2 || $[25] !== t9) {
    t10 = <div className="relative">{t2}{t9}</div>;
    $[24] = t2;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
function SearchFooter() {
  const $ = _c(7);
  const t = useTranslations("search");
  let t0;
  if ($[0] !== t) {
    t0 = t("close_hint");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <span className="text-caption text-foreground-secondary">{t0}</span>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <kbd className="rounded border border-border-primary bg-background-tertiary px-1.5 py-0.5 text-micro font-medium text-foreground-secondary">ESC</kbd>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1) {
    t3 = <div className="hidden items-center justify-between border-t border-border-primary px-4 py-2 sm:flex">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
type TrendingMarket = {
  pubkey: string;
  title: string;
  status: string;
  coverImageUrl: string;
  coverImageNsfw?: boolean;
  volumeTotal: string;
  prizePool: string;
  marketConfig: string;
  ticker?: string;
  outcomeCount?: number;
};
function trendingRow(market: TrendingMarket, shared: RowSharedProps): React.ReactNode {
  return buildMarketRow({
    pubkey: market.pubkey,
    title: market.title,
    category: '',
    status: market.status,
    volumeTotal: market.volumeTotal,
    coverImageUrl: market.coverImageUrl,
    coverImageNsfw: market.coverImageNsfw === true,
    similarity: 0,
    prizePool: market.prizePool,
    marketConfig: market.marketConfig,
    ticker: market.ticker ?? '',
    numOutcomes: market.outcomeCount ?? 0
  }, shared);
}
interface SearchResultsListProps {
  shouldSearch: boolean;
  isLoading: boolean;
  isPending: boolean;
  activeTab: SearchTab;
  activeTabHasResults: boolean;
  hasResults: boolean;
  hasExactMatch: boolean;
  exactMatchPubkey: string | null;
  showFuzzyGroups: boolean;
  mode: string;
  tickerHighlighted: boolean;
  blended: ReadonlyArray<BlendedSearchResult>;
  markets: ReadonlyArray<MarketSearchResult>;
  outcomes: ReadonlyArray<OutcomeSearchResult>;
  winners: ReadonlyArray<WinnerSearchResult>;
  proposals: ReadonlyArray<ProposalSearchResult>;
  creators: ReadonlyArray<CreatorSearchResult>;
  users: ReadonlyArray<UserSearchResult>;
  liveEvents: ReadonlyArray<LiveEventSearchResult>;
  marketByPubkey: Map<string, MarketSearchResult>;
  proposalById: Map<string, ProposalSearchResult>;
  recentEntries: ReadonlyArray<{
    query: string;
    ts: number;
  }>;
  trendingMarkets: ReadonlyArray<TrendingMarket>;
  showTrending: boolean;
  rowShared: RowSharedProps;
  onRecentSelect: (query: string) => void;
  onClearRecent: () => void;
  onCommit: () => void;
  onHoverMarket: (pubkey: string) => void;
  onHoverWinner: (mint: string) => void;
}
function SearchResultsList(props) {
  const $ = _c(138);
  const t = useTranslations("search");
  const tMarkets = useTranslations("markets");
  const tLiveEvents = useTranslations("liveEvents.toolbar");
  const {
    shouldSearch,
    isLoading,
    isPending,
    activeTab,
    activeTabHasResults,
    hasResults,
    hasExactMatch,
    exactMatchPubkey,
    showFuzzyGroups,
    mode,
    tickerHighlighted,
    blended,
    markets,
    outcomes,
    winners,
    proposals,
    creators,
    users,
    liveEvents,
    marketByPubkey,
    proposalById,
    recentEntries,
    trendingMarkets,
    showTrending,
    rowShared,
    onRecentSelect,
    onClearRecent,
    onCommit,
    onHoverMarket,
    onHoverWinner
  } = props;
  let t0;
  if ($[0] !== isLoading) {
    t0 = isLoading ? <div className="flex items-center justify-center py-12"><Icon icon={Loading03Icon} className="size-5 animate-spin text-foreground-secondary" /></div> : null;
    $[0] = isLoading;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== onClearRecent || $[3] !== onRecentSelect || $[4] !== recentEntries || $[5] !== shouldSearch || $[6] !== t) {
    t1 = !shouldSearch && recentEntries.length > 0 && <CommandGroup heading={<div className="flex items-center justify-between pr-2"><span>{t("recent_heading")}</span><AnimatedButton size="xs" variant="tertiary" onClick={() => onClearRecent()} aria-label={t("recent_clear_aria")}>{t("recent_clear")}</AnimatedButton></div>}>{recentEntries.map(entry => <CommandItem key={`${entry.query}-${entry.ts}`} value={entry.query} onSelect={() => onRecentSelect(entry.query)} className="gap-3 rounded-control px-3 py-2.5"><Icon icon={Clock01Icon} className="size-4 text-foreground-secondary" /><span className="truncate text-body">{entry.query}</span></CommandItem>)}</CommandGroup>;
    $[2] = onClearRecent;
    $[3] = onRecentSelect;
    $[4] = recentEntries;
    $[5] = shouldSearch;
    $[6] = t;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== rowShared || $[9] !== shouldSearch || $[10] !== showTrending || $[11] !== tMarkets || $[12] !== trendingMarkets) {
    t2 = !shouldSearch && showTrending && <CommandGroup heading={tMarkets("sort.newest")}>{trendingMarkets.map(m => trendingRow(m, rowShared))}</CommandGroup>;
    $[8] = rowShared;
    $[9] = shouldSearch;
    $[10] = showTrending;
    $[11] = tMarkets;
    $[12] = trendingMarkets;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== isLoading || $[15] !== recentEntries || $[16] !== shouldSearch || $[17] !== showTrending || $[18] !== t) {
    t3 = !shouldSearch && recentEntries.length === 0 && !showTrending && !isLoading ? <div className="py-16 text-center text-body text-foreground-secondary">{t("type_to_search")}</div> : null;
    $[14] = isLoading;
    $[15] = recentEntries;
    $[16] = shouldSearch;
    $[17] = showTrending;
    $[18] = t;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== activeTabHasResults || $[21] !== hasResults || $[22] !== isLoading || $[23] !== isPending || $[24] !== rowShared || $[25] !== shouldSearch || $[26] !== showTrending || $[27] !== t || $[28] !== trendingMarkets) {
    t4 = shouldSearch && !activeTabHasResults && !isPending && !isLoading ? <><div className="flex flex-col items-center justify-center gap-2 py-16 text-center"><Icon icon={SearchRemoveIcon} className="size-8 text-foreground-tertiary" /><p className="text-body font-medium text-foreground-secondary">{t("no_results")}</p><p className="text-caption text-foreground-secondary/60">{t("no_results_hint")}</p></div>{!hasResults && showTrending && <CommandGroup heading={t("try_these_instead")}>{trendingMarkets.map(m_0 => trendingRow(m_0, rowShared))}</CommandGroup>}</> : null;
    $[20] = activeTabHasResults;
    $[21] = hasResults;
    $[22] = isLoading;
    $[23] = isPending;
    $[24] = rowShared;
    $[25] = shouldSearch;
    $[26] = showTrending;
    $[27] = t;
    $[28] = trendingMarkets;
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  let t5;
  if ($[30] !== activeTab || $[31] !== exactMatchPubkey || $[32] !== hasExactMatch || $[33] !== markets || $[34] !== onCommit || $[35] !== onHoverMarket || $[36] !== onHoverWinner || $[37] !== outcomes || $[38] !== proposals || $[39] !== rowShared || $[40] !== t || $[41] !== users || $[42] !== winners) {
    t5 = activeTab === "all" && exactMatchPubkey !== null && hasExactMatch ? <CommandGroup heading={t("exact_match_heading")}>{markets.flatMap(m_1 => m_1.pubkey === exactMatchPubkey ? [buildMarketRow(m_1, rowShared, true)] : [])}{outcomes.flatMap(outcome => outcome.pubkey === exactMatchPubkey ? [<OutcomeRow key={outcome.pubkey} outcome={outcome} href={`/markets/${outcome.market}?outcome=${encodeURIComponent(outcome.label)}`} onCommit={onCommit} onHoverPrefetch={onHoverMarket} />] : [])}{winners.flatMap(winner => winner.pubkey === exactMatchPubkey || winner.mint === exactMatchPubkey ? [<WinnerRow key={winner.pubkey} winner={winner} href={winnerHref(winner)} onCommit={onCommit} onHoverPrefetch={onHoverWinner} />] : [])}{proposals.flatMap(p => p.proposalId === exactMatchPubkey ? [buildProposalRow(p, rowShared)] : [])}{users.filter(u => u.user.walletAddress === exactMatchPubkey).map((entry_0, idx) => buildUserRow(entry_0, idx, rowShared))}</CommandGroup> : null;
    $[30] = activeTab;
    $[31] = exactMatchPubkey;
    $[32] = hasExactMatch;
    $[33] = markets;
    $[34] = onCommit;
    $[35] = onHoverMarket;
    $[36] = onHoverWinner;
    $[37] = outcomes;
    $[38] = proposals;
    $[39] = rowShared;
    $[40] = t;
    $[41] = users;
    $[42] = winners;
    $[43] = t5;
  } else {
    t5 = $[43];
  }
  let t6;
  if ($[44] !== blended || $[45] !== marketByPubkey || $[46] !== mode || $[47] !== onCommit || $[48] !== onHoverMarket || $[49] !== proposalById || $[50] !== rowShared || $[51] !== showFuzzyGroups || $[52] !== t || $[53] !== tickerHighlighted) {
    t6 = showFuzzyGroups && blended.length > 0 && <CommandGroup heading={mode === "ticker" ? t("ticker_matches") : undefined}><BlendedRowsList blended={blended} marketByPubkey={marketByPubkey} proposalById={proposalById} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverMarket={onHoverMarket} onHoverProposal={rowShared.onHoverProposal} /></CommandGroup>;
    $[44] = blended;
    $[45] = marketByPubkey;
    $[46] = mode;
    $[47] = onCommit;
    $[48] = onHoverMarket;
    $[49] = proposalById;
    $[50] = rowShared;
    $[51] = showFuzzyGroups;
    $[52] = t;
    $[53] = tickerHighlighted;
    $[54] = t6;
  } else {
    t6 = $[54];
  }
  let t7;
  if ($[55] !== liveEvents || $[56] !== onCommit || $[57] !== showFuzzyGroups || $[58] !== tLiveEvents) {
    t7 = showFuzzyGroups && liveEvents.length > 0 ? <CommandGroup heading={tLiveEvents("title")}>{liveEvents.map(event => <LiveEventRowSearchItem key={event.id} event={event} onCommit={onCommit} />)}</CommandGroup> : null;
    $[55] = liveEvents;
    $[56] = onCommit;
    $[57] = showFuzzyGroups;
    $[58] = tLiveEvents;
    $[59] = t7;
  } else {
    t7 = $[59];
  }
  let t8;
  if ($[60] !== onCommit || $[61] !== onHoverMarket || $[62] !== outcomes || $[63] !== showFuzzyGroups || $[64] !== t || $[65] !== tickerHighlighted) {
    t8 = showFuzzyGroups && outcomes.length > 0 && <CommandGroup heading={t("group_outcomes")}><OutcomeRowsList outcomes={outcomes} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverMarket={onHoverMarket} /></CommandGroup>;
    $[60] = onCommit;
    $[61] = onHoverMarket;
    $[62] = outcomes;
    $[63] = showFuzzyGroups;
    $[64] = t;
    $[65] = tickerHighlighted;
    $[66] = t8;
  } else {
    t8 = $[66];
  }
  let t9;
  if ($[67] !== onCommit || $[68] !== onHoverWinner || $[69] !== showFuzzyGroups || $[70] !== t || $[71] !== tickerHighlighted || $[72] !== winners) {
    t9 = showFuzzyGroups && winners.length > 0 && <CommandGroup heading={t("group_winners")}><WinnerRowsList winners={winners} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverWinner={onHoverWinner} /></CommandGroup>;
    $[67] = onCommit;
    $[68] = onHoverWinner;
    $[69] = showFuzzyGroups;
    $[70] = t;
    $[71] = tickerHighlighted;
    $[72] = winners;
    $[73] = t9;
  } else {
    t9 = $[73];
  }
  let t10;
  if ($[74] !== creators || $[75] !== onCommit || $[76] !== rowShared || $[77] !== showFuzzyGroups || $[78] !== t) {
    t10 = showFuzzyGroups && creators.length > 0 && <CommandGroup heading={t("group_creators")}><CreatorRowsList creators={creators} onCommit={onCommit} onHoverUser={rowShared.onHoverUser} /></CommandGroup>;
    $[74] = creators;
    $[75] = onCommit;
    $[76] = rowShared;
    $[77] = showFuzzyGroups;
    $[78] = t;
    $[79] = t10;
  } else {
    t10 = $[79];
  }
  let t11;
  if ($[80] !== onCommit || $[81] !== rowShared || $[82] !== showFuzzyGroups || $[83] !== t || $[84] !== users) {
    t11 = showFuzzyGroups && users.length > 0 && <CommandGroup heading={t("group_users")}><UserRowsList users={users} onCommit={onCommit} onHoverUser={rowShared.onHoverUser} /></CommandGroup>;
    $[80] = onCommit;
    $[81] = rowShared;
    $[82] = showFuzzyGroups;
    $[83] = t;
    $[84] = users;
    $[85] = t11;
  } else {
    t11 = $[85];
  }
  let t12;
  if ($[86] !== activeTab || $[87] !== markets || $[88] !== onCommit || $[89] !== onHoverMarket || $[90] !== tickerHighlighted) {
    t12 = activeTab === "markets" && markets.length > 0 && <CommandGroup><MarketRowsList markets={markets} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverMarket={onHoverMarket} /></CommandGroup>;
    $[86] = activeTab;
    $[87] = markets;
    $[88] = onCommit;
    $[89] = onHoverMarket;
    $[90] = tickerHighlighted;
    $[91] = t12;
  } else {
    t12 = $[91];
  }
  let t13;
  if ($[92] !== activeTab || $[93] !== liveEvents || $[94] !== onCommit) {
    t13 = activeTab === "live-events" && liveEvents.length > 0 ? <CommandGroup>{liveEvents.map(event_0 => <LiveEventRowSearchItem key={event_0.id} event={event_0} onCommit={onCommit} />)}</CommandGroup> : null;
    $[92] = activeTab;
    $[93] = liveEvents;
    $[94] = onCommit;
    $[95] = t13;
  } else {
    t13 = $[95];
  }
  let t14;
  if ($[96] !== activeTab || $[97] !== onCommit || $[98] !== onHoverMarket || $[99] !== outcomes || $[100] !== tickerHighlighted) {
    t14 = activeTab === "outcomes" && outcomes.length > 0 && <CommandGroup><OutcomeRowsList outcomes={outcomes} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverMarket={onHoverMarket} /></CommandGroup>;
    $[96] = activeTab;
    $[97] = onCommit;
    $[98] = onHoverMarket;
    $[99] = outcomes;
    $[100] = tickerHighlighted;
    $[101] = t14;
  } else {
    t14 = $[101];
  }
  let t15;
  if ($[102] !== activeTab || $[103] !== onCommit || $[104] !== onHoverWinner || $[105] !== tickerHighlighted || $[106] !== winners) {
    t15 = activeTab === "winners" && winners.length > 0 && <CommandGroup><WinnerRowsList winners={winners} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverWinner={onHoverWinner} /></CommandGroup>;
    $[102] = activeTab;
    $[103] = onCommit;
    $[104] = onHoverWinner;
    $[105] = tickerHighlighted;
    $[106] = winners;
    $[107] = t15;
  } else {
    t15 = $[107];
  }
  let t16;
  if ($[108] !== activeTab || $[109] !== onCommit || $[110] !== proposals || $[111] !== rowShared || $[112] !== tickerHighlighted) {
    t16 = activeTab === "proposals" && proposals.length > 0 && <CommandGroup><ProposalRowsList proposals={proposals} tickerHighlighted={tickerHighlighted} onCommit={onCommit} onHoverProposal={rowShared.onHoverProposal} /></CommandGroup>;
    $[108] = activeTab;
    $[109] = onCommit;
    $[110] = proposals;
    $[111] = rowShared;
    $[112] = tickerHighlighted;
    $[113] = t16;
  } else {
    t16 = $[113];
  }
  let t17;
  if ($[114] !== activeTab || $[115] !== onCommit || $[116] !== rowShared || $[117] !== users) {
    t17 = activeTab === "users" && users.length > 0 && <CommandGroup><UserRowsList users={users} onCommit={onCommit} onHoverUser={rowShared.onHoverUser} /></CommandGroup>;
    $[114] = activeTab;
    $[115] = onCommit;
    $[116] = rowShared;
    $[117] = users;
    $[118] = t17;
  } else {
    t17 = $[118];
  }
  let t18;
  if ($[119] !== t0 || $[120] !== t1 || $[121] !== t10 || $[122] !== t11 || $[123] !== t12 || $[124] !== t13 || $[125] !== t14 || $[126] !== t15 || $[127] !== t16 || $[128] !== t17 || $[129] !== t2 || $[130] !== t3 || $[131] !== t4 || $[132] !== t5 || $[133] !== t6 || $[134] !== t7 || $[135] !== t8 || $[136] !== t9) {
    t18 = <CommandList className="overflow-y-auto p-2 sm:max-h-80">{t0}{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}</CommandList>;
    $[119] = t0;
    $[120] = t1;
    $[121] = t10;
    $[122] = t11;
    $[123] = t12;
    $[124] = t13;
    $[125] = t14;
    $[126] = t15;
    $[127] = t16;
    $[128] = t17;
    $[129] = t2;
    $[130] = t3;
    $[131] = t4;
    $[132] = t5;
    $[133] = t6;
    $[134] = t7;
    $[135] = t8;
    $[136] = t9;
    $[137] = t18;
  } else {
    t18 = $[137];
  }
  return t18;
}
interface SearchActionsParams {
  query: string;
  tickerHighlighted: boolean;
  addRecent: (query: string) => void;
  setQuery: (query: string) => void;
  setDebouncedQuery: (query: string) => void;
  setSearchOpen: (open: boolean) => void;
}
function useSearchActions(t0) {
  const $ = _c(34);
  const {
    query,
    tickerHighlighted,
    addRecent,
    setQuery,
    setDebouncedQuery,
    setSearchOpen
  } = t0;
  const prefetchMarket = usePrefetchGetMarket();
  const prefetchProposal = usePrefetchGetProposal();
  const prefetchUser = usePrefetchGetUser();
  const prefetchWinner = usePrefetchGetWinner();
  const prefetchOutcomeChart = usePrefetchGetOutcomeChart();
  let t1;
  if ($[0] !== prefetchMarket) {
    t1 = function onHoverMarket(pubkey) {
      prefetchMarket({
        pubkey
      });
    };
    $[0] = prefetchMarket;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onHoverMarket = t1;
  let t2;
  if ($[2] !== prefetchOutcomeChart || $[3] !== prefetchWinner) {
    t2 = function onHoverWinner(outcomeMint) {
      prefetchWinner({
        outcomeMint
      });
      prefetchOutcomeChart({
        outcomeMint
      });
    };
    $[2] = prefetchOutcomeChart;
    $[3] = prefetchWinner;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onHoverWinner = t2;
  let t3;
  if ($[5] !== prefetchProposal) {
    t3 = function onHoverProposal(pubkey_0) {
      prefetchProposal({
        pubkey: pubkey_0,
        fields: PROPOSAL_DETAIL_PREFETCH_FIELDS
      });
    };
    $[5] = prefetchProposal;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const onHoverProposal = t3;
  let t4;
  if ($[7] !== prefetchUser) {
    t4 = function onHoverUser(identifier) {
      prefetchUser({
        identifier,
        fields: PORTFOLIO_USER_PROFILE_FIELDS
      });
    };
    $[7] = prefetchUser;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const onHoverUser = t4;
  let t5;
  if ($[9] !== addRecent || $[10] !== query || $[11] !== setSearchOpen) {
    t5 = function commitSearch() {
      if (query.trim().length > 0) {
        addRecent(query);
      }
      setSearchOpen(false);
    };
    $[9] = addRecent;
    $[10] = query;
    $[11] = setSearchOpen;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const commitSearch = t5;
  let t6;
  if ($[13] !== setDebouncedQuery || $[14] !== setQuery) {
    t6 = function handleRecentSelect(recentQuery) {
      setQuery(recentQuery);
      setDebouncedQuery(recentQuery);
    };
    $[13] = setDebouncedQuery;
    $[14] = setQuery;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleRecentSelect = t6;
  let t7;
  if ($[16] !== setSearchOpen) {
    t7 = function handleOpenChange(open) {
      setSearchOpen(open);
    };
    $[16] = setSearchOpen;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleOpenChange = t7;
  let t8;
  if ($[18] !== setDebouncedQuery || $[19] !== setQuery) {
    t8 = async function handlePaste() {
      try {
        const text = await navigator.clipboard.readText();
        const trimmed = text.trim();
        if (trimmed.length > 0) {
          setQuery(trimmed);
          setDebouncedQuery(trimmed);
        }
      } catch {}
    };
    $[18] = setDebouncedQuery;
    $[19] = setQuery;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handlePaste = t8;
  let t9;
  if ($[21] !== commitSearch || $[22] !== onHoverMarket || $[23] !== onHoverProposal || $[24] !== onHoverUser || $[25] !== onHoverWinner || $[26] !== tickerHighlighted) {
    t9 = {
      tickerHighlighted,
      onCommit: commitSearch,
      onHoverMarket,
      onHoverWinner,
      onHoverProposal,
      onHoverUser
    };
    $[21] = commitSearch;
    $[22] = onHoverMarket;
    $[23] = onHoverProposal;
    $[24] = onHoverUser;
    $[25] = onHoverWinner;
    $[26] = tickerHighlighted;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== commitSearch || $[29] !== handleOpenChange || $[30] !== handlePaste || $[31] !== handleRecentSelect || $[32] !== t9) {
    t10 = {
      rowShared: t9,
      commitSearch,
      handleRecentSelect,
      handleOpenChange,
      handlePaste
    };
    $[28] = commitSearch;
    $[29] = handleOpenChange;
    $[30] = handlePaste;
    $[31] = handleRecentSelect;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  return t10;
}
export function SearchModal(t0) {
  const $ = _c(76);
  const {
    liveEvents: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const liveEvents = t2;
  const t = useTranslations("search");
  const tLiveEvents = useTranslations("liveEvents.toolbar");
  const searchOpen = useLayoutStore(_temp);
  const searchInitialTab = useLayoutStore(_temp2);
  const setSearchOpen = useLayoutStore(_temp3);
  const liveEventsEnabled = useFeatureGate("live_events") === true;
  const recentEntries = useRecentSearchesStore(_temp4);
  const addRecent = useRecentSearchesStore(_temp5);
  const clearRecent = useRecentSearchesStore(_temp6);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const debounceTimerRef = useRef(null);
  const {
    mode,
    cleanQuery
  } = useSearchQueryMode(debouncedQuery);
  const tickerHighlighted = mode === "ticker";
  let t3;
  let t4;
  if ($[2] !== query) {
    t3 = () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        setDebouncedQuery(query);
      }, DEBOUNCE_MS);
      return () => {
        if (debounceTimerRef.current !== null) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    };
    t4 = [query];
    $[2] = query;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== liveEventsEnabled || $[6] !== searchInitialTab || $[7] !== searchOpen) {
    t5 = () => {
      if (!searchOpen) {
        return;
      }
      setQuery("");
      setDebouncedQuery("");
      setActiveTab(liveEventsEnabled ? searchInitialTab : "all");
    };
    t6 = [liveEventsEnabled, searchInitialTab, searchOpen];
    $[5] = liveEventsEnabled;
    $[6] = searchInitialTab;
    $[7] = searchOpen;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useLayoutEffect(t5, t6);
  const shouldSearch = cleanQuery.length >= MIN_QUERY_LENGTH;
  let t7;
  if ($[10] !== cleanQuery) {
    t7 = {
      q: cleanQuery,
      type: "all"
    };
    $[10] = cleanQuery;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = shouldSearch && searchOpen && activeTab !== "live-events";
  let t9;
  if ($[12] !== t8) {
    t9 = {
      enabled: t8
    };
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const {
    data,
    isPending
  } = useGetSearch(t7, t9);
  let t10;
  if ($[14] !== data?.blended) {
    t10 = data?.blended ?? [];
    $[14] = data?.blended;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const blended = t10;
  const markets = data?.markets ?? [];
  let t11;
  if ($[16] !== data?.outcomes) {
    t11 = data?.outcomes ?? [];
    $[16] = data?.outcomes;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const outcomes = t11;
  let t12;
  if ($[18] !== data?.winners) {
    t12 = data?.winners ?? [];
    $[18] = data?.winners;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const winners = t12;
  const proposals = data?.proposals ?? [];
  let t13;
  if ($[20] !== data?.creators) {
    t13 = data?.creators ?? [];
    $[20] = data?.creators;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const creators = t13;
  let t14;
  if ($[22] !== data?.users) {
    t14 = data?.users ?? [];
    $[22] = data?.users;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const users = t14;
  let t15;
  if ($[24] !== cleanQuery || $[25] !== liveEvents || $[26] !== liveEventsEnabled || $[27] !== shouldSearch) {
    t15 = liveEventsEnabled && shouldSearch ? liveEvents.filter(event => event.title.toLowerCase().includes(cleanQuery.toLowerCase())) : [];
    $[24] = cleanQuery;
    $[25] = liveEvents;
    $[26] = liveEventsEnabled;
    $[27] = shouldSearch;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const matchingLiveEvents = t15;
  const isLoading = shouldSearch && activeTab !== "live-events" && isPending;
  const hasResults = blended.length > 0 || markets.length > 0 || outcomes.length > 0 || winners.length > 0 || proposals.length > 0 || creators.length > 0 || users.length > 0 || matchingLiveEvents.length > 0;
  const exactMatchPubkey = mode === "ca" ? cleanQuery : null;
  const hasExactMatch = exactMatchPubkey !== null && (markets.some(m => m.pubkey === exactMatchPubkey) || outcomes.some(o => o.pubkey === exactMatchPubkey) || winners.some(w => w.pubkey === exactMatchPubkey || w.mint === exactMatchPubkey) || proposals.some(p => p.proposalId === exactMatchPubkey) || users.some(u => u.user.walletAddress === exactMatchPubkey));
  const activeTabHasResults = activeTab === "all" ? mode === "ca" ? hasExactMatch : blended.length > 0 || outcomes.length > 0 || winners.length > 0 || creators.length > 0 || users.length > 0 || matchingLiveEvents.length > 0 : activeTab === "markets" ? markets.length > 0 : activeTab === "outcomes" ? outcomes.length > 0 : activeTab === "live-events" ? matchingLiveEvents.length > 0 : activeTab === "winners" ? winners.length > 0 : activeTab === "proposals" ? proposals.length > 0 : users.length > 0;
  const tabOptions = liveEventsEnabled ? [...BASE_TAB_OPTIONS.slice(0, 2), "live-events" as const, ...BASE_TAB_OPTIONS.slice(2)] : BASE_TAB_OPTIONS;
  useEffect(() => {
    if (hasExactMatch) {
      showToast({
        id: TOAST_IDS.SEARCH_EXACT_MATCH,
        message: t("exact_match_found"),
        variant: "success"
      });
    }
  }, [hasExactMatch, t]);
  const showFuzzyGroups = activeTab === "all" && mode !== "ca";
  const marketByPubkey = new Map(markets.map(_temp7));
  const proposalById = new Map(proposals.map(_temp8));
  const isNoResultsState = shouldSearch && !isPending && !hasResults;
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      sortBy: "createdAt",
      sortOrder: "desc",
      status: TRENDING_SEARCH_STATUS
    };
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== searchOpen) {
    t17 = {
      enabled: searchOpen
    };
    $[30] = searchOpen;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const {
    data: trendingPage
  } = useGetMarkets(t16, t17);
  let t18;
  if ($[32] !== trendingPage?.data) {
    t18 = trendingPage?.data ?? [];
    $[32] = trendingPage?.data;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t18) {
    t19 = t18.slice(0, TRENDING_SEARCH_LIMIT);
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const trendingMarkets = t19;
  const showTrending = trendingMarkets.length > 0;
  let t20;
  if ($[36] !== addRecent || $[37] !== query || $[38] !== setSearchOpen || $[39] !== tickerHighlighted) {
    t20 = {
      query,
      tickerHighlighted,
      addRecent,
      setQuery,
      setDebouncedQuery,
      setSearchOpen
    };
    $[36] = addRecent;
    $[37] = query;
    $[38] = setSearchOpen;
    $[39] = tickerHighlighted;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const {
    rowShared,
    commitSearch,
    handleRecentSelect,
    handleOpenChange,
    handlePaste
  } = useSearchActions(t20);
  let t21;
  if ($[41] !== t) {
    t21 = t("title");
    $[41] = t;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t) {
    t22 = t("description");
    $[43] = t;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== activeTab || $[46] !== activeTabHasResults || $[47] !== hasResults || $[48] !== isLoading || $[49] !== isNoResultsState || $[50] !== mode || $[51] !== recentEntries.length || $[52] !== showTrending) {
    t23 = [hasResults, activeTabHasResults, isLoading, isNoResultsState, activeTab, recentEntries.length, showTrending, mode];
    $[45] = activeTab;
    $[46] = activeTabHasResults;
    $[47] = hasResults;
    $[48] = isLoading;
    $[49] = isNoResultsState;
    $[50] = mode;
    $[51] = recentEntries.length;
    $[52] = showTrending;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = cn("flex flex-col pt-2", "**:data-[slot=command-input-wrapper]:border-b");
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== handlePaste) {
    t25 = () => {
      handlePaste();
    };
    $[55] = handlePaste;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== mode || $[58] !== query || $[59] !== t25) {
    t26 = <SearchInputRow query={query} mode={mode} onQueryChange={setQuery} onPaste={t25} />;
    $[57] = mode;
    $[58] = query;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  const t27 = "flex items-center gap-2 px-3 py-2";
  const t28 = "min-w-0 flex-1";
  const T0 = SortPillRow;
  const t29 = "sm";
  const t30 = "search-modal-active-tab";
  let t31;
  if ($[61] !== t || $[62] !== tLiveEvents) {
    t31 = tab => ({
      value: tab,
      label: tab === "live-events" ? tLiveEvents("title") : t(`tab_${tab}`)
    });
    $[61] = t;
    $[62] = tLiveEvents;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  const t32 = tabOptions.map(t31);
  let t33;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = value => setActiveTab(value as SearchTab);
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  let t34;
  if ($[65] !== T0 || $[66] !== activeTab || $[67] !== t32) {
    t34 = <div className={t28}><T0 size={t29} layoutId={t30} options={t32} activeValue={activeTab} onChange={t33} /></div>;
    $[65] = T0;
    $[66] = activeTab;
    $[67] = t32;
    $[68] = t34;
  } else {
    t34 = $[68];
  }
  let t35;
  if ($[69] !== activeTab) {
    t35 = <SearchGlobeButton activeTab={activeTab} />;
    $[69] = activeTab;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  let t36;
  if ($[71] !== t34 || $[72] !== t35) {
    t36 = <div className={t27}>{t34}{t35}</div>;
    $[71] = t34;
    $[72] = t35;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div className="border-t border-border-primary" />;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <SearchFooter />;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  return <SilkSheet open={searchOpen} onOpenChange={handleOpenChange} variant="detached" size="lg" keyboard={true} title={t21} description={t22} srOnlyHeader={true} closeButton={false} contentClassName={DIALOG_CONTENT_CLASS}><MotionAnimatedAutoHeight layoutDependencies={t23}><Command shouldFilter={false} className={t24}>{t26}{t36}{t37}<SearchResultsList shouldSearch={shouldSearch} isLoading={isLoading} isPending={isPending} activeTab={activeTab} activeTabHasResults={activeTabHasResults} hasResults={hasResults} hasExactMatch={hasExactMatch} exactMatchPubkey={exactMatchPubkey} showFuzzyGroups={showFuzzyGroups} mode={mode} tickerHighlighted={tickerHighlighted} blended={blended} markets={markets} outcomes={outcomes} winners={winners} proposals={proposals} creators={creators} users={users} liveEvents={matchingLiveEvents} marketByPubkey={marketByPubkey} proposalById={proposalById} recentEntries={recentEntries} trendingMarkets={trendingMarkets} showTrending={showTrending} rowShared={rowShared} onRecentSelect={handleRecentSelect} onClearRecent={clearRecent} onCommit={commitSearch} onHoverMarket={rowShared.onHoverMarket} onHoverWinner={rowShared.onHoverWinner} /></Command>{t38}</MotionAnimatedAutoHeight></SilkSheet>;
}
function _temp8(p_0) {
  return [p_0.proposalId, p_0];
}
function _temp7(m_0) {
  return [m_0.pubkey, m_0];
}
function _temp6(s_4) {
  return s_4.clearRecent;
}
function _temp5(s_3) {
  return s_3.addRecent;
}
function _temp4(s_2) {
  return s_2.entries;
}
function _temp3(s_1) {
  return s_1.setSearchOpen;
}
function _temp2(s_0) {
  return s_0.searchInitialTab;
}
function _temp(s) {
  return s.searchOpen;
}